import { eyeIcon, LoadIcon } from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import { useI18n } from "@excalidraw/excalidraw/i18n";
import React, { useEffect, useState } from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import { LanguageList } from "../app-language/LanguageList";
import {
  getCurrentProjectId,
  getCurrentWorkspaceId,
  getProject,
  getWorkspace,
} from "../data/projectsStorage";

import { saveDebugState } from "./DebugCanvas";

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  onOpenProjects?: () => void;
  workspaceVersion?: number;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  setTheme: (theme: Theme | "system") => void;
  refresh: () => void;
}> = React.memo((props) => {
  const { t } = useI18n();
  const [workspaceLabel, setWorkspaceLabel] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const projectId = getCurrentProjectId();
    const workspaceId = getCurrentWorkspaceId();
    if (!projectId || !workspaceId) {
      setWorkspaceLabel(null);
      return;
    }
    Promise.all([getProject(projectId), getWorkspace(workspaceId)]).then(
      ([project, workspace]) => {
        if (cancelled || !project || !workspace) {
          return;
        }
        setWorkspaceLabel(`${project.name} › ${workspace.name}`);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [props.refresh, props.workspaceVersion]);

  return (
    <MainMenu>
      {props.onOpenProjects && (
        <MainMenu.Item icon={LoadIcon} onSelect={props.onOpenProjects}>
          {workspaceLabel ?? t("projectsHome.title")}
        </MainMenu.Item>
      )}
      <MainMenu.DefaultItems.LoadScene />
      <MainMenu.DefaultItems.SaveToActiveFile />
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />
      {props.isCollabEnabled && (
        <MainMenu.DefaultItems.LiveCollaborationTrigger
          isCollaborating={props.isCollaborating}
          onSelect={() => props.onCollabDialogOpen()}
        />
      )}
      <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />
      {isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onSelect={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
      <MainMenu.Separator />
      <MainMenu.DefaultItems.Preferences />
      <MainMenu.DefaultItems.ToggleTheme
        allowSystemTheme
        theme={props.theme}
        onSelect={props.setTheme}
      />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
    </MainMenu>
  );
});
